/**
 * 項目バリデーション（#3357）の判定ロジック。
 *
 * 文字種・入力パターン（正規表現）・日付範囲の3つを、外部依存のない純粋関数として持つ。
 * 同じ判定を「結果画面の yup スキーマ」「明細セルの検証」「要注意（reviewIssues）」の
 * 3箇所から呼ぶため、実装をここ1つに集約する。
 *
 * ⚠️ functions/src/utils/pattern/itemValidationRules.ts と同一のロジック。
 *    片方だけ変更しないこと（共通テストケース JSON で両実装の一致を検証している）。
 */

import {
  ITEM_VALIDATION_PATTERN_MAX_LENGTH,
  ITEM_VALIDATION_TEXT_MAX_LENGTH,
  charTypePattern,
  dateRangeUnit,
  itemValidationResultMessage,
  type DateRangeUnit,
  type ItemCharType,
} from '../../constants/itemValidation';

/**
 * 判定に渡す日付範囲の設定。
 * 画面（camelCase）と Firestore（snake_case）で持ち方が違うため、
 * 呼び出し側がこの形に詰め替えてから渡す。
 */
export interface DateRangeRule {
  /** 過去を許さない（基準日より前をエラー） */
  noPast?: boolean;
  /** 未来を許さない（基準日より後をエラー） */
  noFuture?: boolean;
  /** さかのぼれる上限の数値 */
  pastLimit?: number;
  /** さかのぼれる上限の単位 */
  pastUnit?: DateRangeUnit;
  /** 先に進める上限の数値 */
  futureLimit?: number;
  /** 先に進める上限の単位 */
  futureUnit?: DateRangeUnit;
}

/** 日付範囲の判定結果。どの条件で外れたかを呼び出し側がメッセージに使う */
export type DateRangeViolation = 'past' | 'future' | 'past_limit' | 'future_limit';

/** 値が空（未入力）か。空値はどの制約でも対象外にする */
const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * 文字種の制約を満たすか（#3357）。
 *
 * 空値・未設定は true（制約なしとして扱う）。空欄の扱いは必須制約に委ねる。
 * 文字数の上限を超える値は判定せず true にする（ReDoS 対策。上限超過自体は
 * 文字数制約で検出される）。
 */
export const matchesCharType = (value: unknown, charType?: ItemCharType): boolean => {
  if (charType === undefined || isBlank(value)) return true;

  const text = String(value);
  if (text.length > ITEM_VALIDATION_TEXT_MAX_LENGTH) return true;

  const pattern = charTypePattern[charType];
  if (pattern === undefined) return true;

  return pattern.test(text);
};

/**
 * 入力パターン（正規表現）を満たすか（#3357）。
 *
 * 空値・未設定は true。不正な正規表現も true（検証しない）にする。
 * パターンの正しさは保存時に検証しており、ここで落とすと
 * 「設定ミスのせいで全件がエラー」になって原因が分かりにくいため。
 */
export const matchesPattern = (value: unknown, pattern?: string): boolean => {
  if (pattern === undefined || pattern === '' || isBlank(value)) return true;
  if (pattern.length > ITEM_VALIDATION_PATTERN_MAX_LENGTH) return true;

  const text = String(value);
  if (text.length > ITEM_VALIDATION_TEXT_MAX_LENGTH) return true;

  try {
    return new RegExp(pattern).test(text);
  } catch {
    return true;
  }
};

/**
 * JST（UTC+9）のミリ秒オフセット。
 *
 * 判定は必ず日本時間の「日付」で行う。getFullYear() などはローカルタイムゾーンに
 * 依存するため、そのまま使うとフロント（利用者のPC設定＝通常JST）と
 * Cloud Functions（UTC）で「今日」が1日ずれる。
 * 例: 9/29 8:00 JST は UTC では 9/28 のため、同じ結果でも判定が食い違う。
 * 日本国内向けのサービスのため夏時間は考慮しない（JST は年間を通じて UTC+9）。
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/**
 * JST の暦日を表す数値（1970-01-01 からの日数）にする。
 *
 * 時刻を落として日単位で比較するために使う。Date のローカルメソッドを使わず、
 * UTC のミリ秒に JST のオフセットを足してから日数に丸めることで、
 * 実行環境のタイムゾーンによらず同じ結果になる。
 */
const toJstDayNumber = (date: Date): number =>
  Math.floor((date.getTime() + JST_OFFSET_MS) / 86400000);

/** JST の暦日数値を Date（その日の JST 0時）に戻す */
const fromJstDayNumber = (dayNumber: number): Date =>
  new Date(dayNumber * 86400000 - JST_OFFSET_MS);

/**
 * 基準日から単位ぶん進めた（戻した）JST の暦日数値を返す。
 * 月・年は月末の繰り上がりを Date に任せる（2/29 の1年前が 3/1 になる等）。
 */
const shiftJstDay = (baseDayNumber: number, amount: number, unit: DateRangeUnit): number => {
  const base = fromJstDayNumber(baseDayNumber);
  // JST の暦日で加減算するため、UTC メソッドで「JST 0時をずらした時刻」を操作する
  const shifted = new Date(base.getTime() + JST_OFFSET_MS);
  switch (unit) {
    case dateRangeUnit.day:
      shifted.setUTCDate(shifted.getUTCDate() + amount);
      break;
    case dateRangeUnit.month:
      shifted.setUTCMonth(shifted.getUTCMonth() + amount);
      break;
    case dateRangeUnit.year:
      shifted.setUTCFullYear(shifted.getUTCFullYear() + amount);
      break;
  }
  return Math.floor(shifted.getTime() / 86400000);
};

/**
 * 日付が許容範囲に収まるか（#3357）。外れた場合はその理由を返す。
 *
 * 基準日（baseDate）は呼び出し側が渡す。相対指定（「1年以上前はNG」）のため
 * 「いつを今日とするか」で結果が変わり、暗黙に new Date() を使うと
 * 要注意（サーバで判定・保存）と画面表示（閲覧時に判定）で食い違いが起きる。
 * 判定を1つに保つため、どちらも結果ドキュメントの created_at（OCR処理日）を渡す。
 *
 * @param value 判定する日付。解釈できない値は null（制約の対象外）を返す
 * @param rule 日付範囲の設定
 * @param baseDate 基準日（OCR処理日）
 * @returns 範囲内なら null、外れていれば違反の種別
 */
export const checkDateRange = (
  value: unknown,
  rule: DateRangeRule,
  baseDate: Date,
): DateRangeViolation | null => {
  if (isBlank(value)) return null;

  const target = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(target.getTime())) return null; // 日付として解釈できない値は型チェックに委ねる

  // JST の暦日どうしで比較する（実行環境のタイムゾーンに依存させない）
  const targetDay = toJstDayNumber(target);
  const baseDay = toJstDayNumber(baseDate);

  if (rule.noPast === true && targetDay < baseDay) {
    return 'past';
  }
  if (rule.noFuture === true && targetDay > baseDay) {
    return 'future';
  }
  if (rule.pastLimit !== undefined && rule.pastUnit !== undefined) {
    if (targetDay < shiftJstDay(baseDay, -rule.pastLimit, rule.pastUnit)) {
      return 'past_limit';
    }
  }
  if (rule.futureLimit !== undefined && rule.futureUnit !== undefined) {
    if (targetDay > shiftJstDay(baseDay, rule.futureLimit, rule.futureUnit)) {
      return 'future_limit';
    }
  }

  return null;
};

/** 日付範囲を満たすか（違反の種別が不要な呼び出し向け） */
export const withinDateRange = (value: unknown, rule: DateRangeRule, baseDate: Date): boolean =>
  checkDateRange(value, rule, baseDate) === null;

/**
 * 日付範囲の違反種別から表示メッセージを作る（#3357）。
 * yup スキーマ・明細セル・要注意の3箇所で同じ文言を使うため、ここに集約する。
 */
export const dateRangeMessage = (violation: DateRangeViolation, rule: DateRangeRule): string => {
  switch (violation) {
    case 'past':
      return itemValidationResultMessage.dateNoPast;
    case 'future':
      return itemValidationResultMessage.dateNoFuture;
    case 'past_limit':
      return itemValidationResultMessage.datePastLimit(
        rule.pastLimit ?? 0,
        rule.pastUnit ?? dateRangeUnit.day,
      );
    case 'future_limit':
      return itemValidationResultMessage.dateFutureLimit(
        rule.futureLimit ?? 0,
        rule.futureUnit ?? dateRangeUnit.day,
      );
  }
};
